'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import Cookies from 'js-cookie';
import { useAuthStore } from '@/stores/auth';

/**
 * Active context type
 * - PERSONAL: User-specific data (favorites, my cruises, etc.)
 * - ORGANIZER: Data for a specific organizer (from organizers table)
 * - ADMIN: Admin dashboard with approvals and admin functions
 */
export type ContextType = 'PERSONAL' | 'ORGANIZER' | 'ADMIN';

/**
 * Active context structure
 * - type: The context type (PERSONAL or ORGANIZER)
 * - id: Organizer ID (only present when type is ORGANIZER)
 * - name: Organizer name (optional, for display purposes)
 */
export interface ActiveContext {
  type: ContextType;
  id?: string;
  name?: string;
}

interface DashboardContextValue {
  activeContext: ActiveContext;
  setContext: (context: ActiveContext) => void;
  setPersonalContext: () => void;
  setOrganizerContext: (id: string, name?: string) => void;
  setAdminContext: () => void;
  isPersonalContext: boolean;
  isOrganizerContext: boolean;
  isAdminContext: boolean;
  activeOrganizerId: string | null;
}

const DashboardContext = createContext<DashboardContextValue | undefined>(undefined);

// Cookie and localStorage keys
const CONTEXT_COOKIE_KEY = 'vesselly_context_id';
const CONTEXT_STORAGE_KEY = 'vesselly-active-context';

// Cookie expiration (30 days)
const COOKIE_EXPIRES_DAYS = 30;

/**
 * Default context (PERSONAL)
 */
const DEFAULT_CONTEXT: ActiveContext = {
  type: 'PERSONAL',
};

/**
 * Parse context from stored value
 * Expected format: "PERSONAL" or "ORGANIZER:{id}" or "ORGANIZER:{id}:{name}" or "ADMIN"
 */
function parseStoredContext(value: string | null): ActiveContext {
  if (!value || value === 'PERSONAL' || value === '__personal__') {
    return DEFAULT_CONTEXT;
  }

  // Handle ADMIN format
  if (value === 'ADMIN') {
    return {
      type: 'ADMIN',
    };
  }

  // Handle ORGANIZER format: "ORGANIZER:{id}" or "ORGANIZER:{id}:{name}"
  if (value.startsWith('ORGANIZER:')) {
    const parts = value.split(':');
    if (parts.length >= 2 && parts[1]) {
      return {
        type: 'ORGANIZER',
        id: parts[1],
        name: parts[2] || undefined,
      };
    }
  }

  // Legacy format: just an ID (treat as ORGANIZER)
  if (value && value !== '__private__') {
    return {
      type: 'ORGANIZER',
      id: value,
    };
  }

  return DEFAULT_CONTEXT;
}

/**
 * Serialize context to string for storage
 */
function serializeContext(context: ActiveContext): string {
  if (context.type === 'PERSONAL') {
    return 'PERSONAL';
  }

  if (context.type === 'ADMIN') {
    return 'ADMIN';
  }

  if (context.type === 'ORGANIZER' && context.id) {
    // Format: "ORGANIZER:{id}" or "ORGANIZER:{id}:{name}"
    return context.name 
      ? `ORGANIZER:${context.id}:${context.name}`
      : `ORGANIZER:${context.id}`;
  }

  return 'PERSONAL';
}

/**
 * Get active context from cookie or localStorage
 * Prioritizes localStorage since it contains the full context information (including ADMIN)
 */
function getStoredContext(): ActiveContext {
  if (typeof window === 'undefined') return DEFAULT_CONTEXT;

  // Always check localStorage first - it has the complete context information
  // This is especially important for ADMIN context which isn't properly stored in cookie
  try {
    const stored = localStorage.getItem(CONTEXT_STORAGE_KEY);
    if (stored) {
      return parseStoredContext(stored);
    }
  } catch (error) {
    console.warn('Failed to read active context from localStorage:', error);
  }

  // Fallback to cookie (for backward compatibility and backend access)
  // Cookie mainly stores ORGANIZER ID or __personal__ (which could mean PERSONAL or ADMIN)
  const cookieValue = Cookies.get(CONTEXT_COOKIE_KEY);
  if (cookieValue && cookieValue !== '__personal__') {
    // Only use cookie if it's an ORGANIZER ID (not the generic __personal__)
    return parseStoredContext(cookieValue);
  }

  return DEFAULT_CONTEXT;
}

/**
 * Save active context to both cookie and localStorage
 */
function saveContext(context: ActiveContext): void {
  if (typeof window === 'undefined') return;

  const serialized = serializeContext(context);

  // Save to cookie (for backend access)
  // Backend expects just the ID for ORGANIZER, or empty/null for PERSONAL/ADMIN
  const cookieValue = context.type === 'ORGANIZER' && context.id 
    ? context.id 
    : '__personal__';

  Cookies.set(CONTEXT_COOKIE_KEY, cookieValue, {
    expires: COOKIE_EXPIRES_DAYS,
    sameSite: 'lax',
    secure: process.env.NODE_ENV === 'production',
  });

  // Always save full context to localStorage (for client-side access and persistence)
  // This is especially important for ADMIN context which isn't stored in cookie
  try {
    localStorage.setItem(CONTEXT_STORAGE_KEY, serialized);
  } catch (error) {
    console.warn('Failed to save active context to localStorage:', error);
  }
}

interface DashboardProviderProps {
  children: ReactNode;
}

/**
 * Dashboard Context Provider
 * Manages active context state and syncs with cookies and localStorage
 */
export function DashboardProvider({ children }: DashboardProviderProps) {
  const authStore = useAuthStore();
  // Use DEFAULT_CONTEXT for initial state so server and client first render match (avoids hydration mismatch).
  // Restore from storage in useEffect so context is correct after mount.
  const [activeContext, setActiveContextState] = useState<ActiveContext>(DEFAULT_CONTEXT);
  const isRestoredRef = useRef(false);

  // Restore context from storage on mount; sync with storage when it changes (e.g., from other tabs)
  useEffect(() => {
    const stored = getStoredContext();
    setActiveContextState(stored);
    isRestoredRef.current = true;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === CONTEXT_STORAGE_KEY && e.newValue) {
        const newContext = parseStoredContext(e.newValue);
        setActiveContextState(newContext);
        saveContext(newContext); // Update cookie
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Sync dashboard context from auth store when activeOrganizationId changes (e.g. after clicking org in Settings or org panel redirect).
  // Do not overwrite ADMIN context so user's admin view choice is preserved.
  useEffect(() => {
    if (!isRestoredRef.current || authStore.authLoading) return;

    const orgId = authStore.activeOrganizationId;
    setActiveContextState((prev) => {
      if (prev.type === 'ADMIN') return prev;
      if (orgId) {
        if (prev.type === 'ORGANIZER' && prev.id === orgId) return prev;
        const membership = authStore.user?.memberships?.find((m) => m.organization.id === orgId);
        const name = membership?.organization.name;
        const next: ActiveContext = { type: 'ORGANIZER', id: orgId, name };
        saveContext(next);
        return next;
      }
      if (prev.type === 'PERSONAL') return prev;
      saveContext(DEFAULT_CONTEXT);
      return DEFAULT_CONTEXT;
    });
  }, [authStore.activeOrganizationId, authStore.authLoading, authStore.user?.memberships]);

  /**
   * Set active context
   * @param context - The context to set
   */
  const setContext = useCallback((context: ActiveContext) => {
    // Validate context
    if (context.type === 'ORGANIZER' && !context.id) {
      console.warn('Organizer ID is required for ORGANIZER context');
      return;
    }

    setActiveContextState(context);
    saveContext(context);

    // Dispatch custom event for other components to react
    window.dispatchEvent(new CustomEvent('dashboard-context-changed', { 
      detail: context 
    }));
  }, []);

  /**
   * Set context to PERSONAL
   */
  const setPersonalContext = useCallback(() => {
    setContext(DEFAULT_CONTEXT);
  }, [setContext]);

  /**
   * Set context to ORGANIZER
   * @param id - Organizer ID (required)
   * @param name - Organizer name (optional, for display)
   */
  const setOrganizerContext = useCallback((id: string, name?: string) => {
    setContext({
      type: 'ORGANIZER',
      id,
      name,
    });
  }, [setContext]);

  /**
   * Set context to ADMIN
   */
  const setAdminContext = useCallback(() => {
    setContext({
      type: 'ADMIN',
    });
  }, [setContext]);

  // Computed values
  const isPersonalContext = activeContext.type === 'PERSONAL';
  const isOrganizerContext = activeContext.type === 'ORGANIZER';
  const isAdminContext = activeContext.type === 'ADMIN';
  const activeOrganizerId = isOrganizerContext ? (activeContext.id || null) : null;

  const value: DashboardContextValue = {
    activeContext,
    setContext,
    setPersonalContext,
    setOrganizerContext,
    setAdminContext,
    isPersonalContext,
    isOrganizerContext,
    isAdminContext,
    activeOrganizerId,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
}

/**
 * Hook to access dashboard context
 * @throws Error if used outside DashboardProvider
 */
export function useDashboardMode(): DashboardContextValue {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboardMode must be used within a DashboardProvider');
  }
  return context;
}
